import {
  type HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { SIGNAL_R_STATUS } from '~/common/constants';
import useAuthentication from '~/common/hooks/useAuthentication';
import { key as loanSummaryKey } from '~/common/hooks/useGetLoanSummary';
import useUrl from '~/common/hooks/useUrl';
import { useSignalRStore } from '~/stores/useSignalRStore';
import type {
  Notification,
  NotificationMessage,
} from '~/stores/useSignalRStore';

// Chrome 149+ closes active WebSockets when a page enters bfcache (ws.onclose
// fires, SignalR goes Disconnected). The default policy stops after 4 retries;
// this retries indefinitely so the reconnect survives until the hub responds.
const backoffPolicy = {
  nextRetryDelayInMilliseconds: (retryContext: {
    previousRetryCount: number;
  }) => Math.min(1_000 * 2 ** retryContext.previousRetryCount, 30_000),
};

const useSignalR = () => {
  const { obfuscatedLoanIdentifier } = useUrl();
  const [notification, setNotification] = useState<Notification>(null);
  const queryClient = useQueryClient();
  const { user, isAuthorized } = useAuthentication();
  const setIsConnected = useSignalRStore((s) => s.setIsConnected);

  // Ref so the pageshow handler (registered in a separate effect) can reach
  // the live connection instance across renders.
  const connectionRef = useRef<HubConnection | null>(null);

  const startConnection = useCallback(async () => {
    if (!isAuthorized || !obfuscatedLoanIdentifier) {
      return;
    }

    // Don't open a second connection while one is already live.
    const state = connectionRef.current?.state;
    if (
      state === HubConnectionState.Connected ||
      state === HubConnectionState.Connecting ||
      state === HubConnectionState.Reconnecting
    ) {
      return;
    }

    const { obfuscatedLenderDatabaseId, obfuscatedLoanRecordId } =
      obfuscatedLoanIdentifier;

    // build signalR connection hub
    const connection = new HubConnectionBuilder()
      .withUrl(
        `${globalThis.globalConfig.signalR.endpoint_uri}?obfuscatedLenderDatabaseId=${obfuscatedLenderDatabaseId}&obfuscatedLoanRecordId=${obfuscatedLoanRecordId}`,
        {
          accessTokenFactory: () => user?.access_token,
          skipNegotiation: false,
        },
      )
      .withAutomaticReconnect(backoffPolicy)
      .configureLogging(globalThis.globalConfig.signalR.logLevel)
      .build();

    // Set server timeout and keepalive interval after build
    connection.serverTimeoutInMilliseconds = 60000; // 60 seconds server timeout
    connection.keepAliveIntervalInMilliseconds = 15000; // Send keepalive ping every 15 seconds

    // set up notification watcher
    /* c8 ignore next */
    connection.on(
      'WebNotification',
      (
        _obfuscatedLenderDatabaseId: number,
        _obfuscatedLoanRecordId: number,
        notificationMessage: NotificationMessage,
        correlationId: number,
        receivedDateTime: string,
      ) => {
        if (globalThis.globalConfig.signalR.logLevel < LogLevel.Error) {
          /* eslint-disable no-console*/
          console.log('*** Web Notification received ***');
          console.log(
            'notificationType',
            typeof notificationMessage === 'number'
              ? notificationMessage
              : notificationMessage?.notificationType,
          );
          console.log('receivedDateTime', receivedDateTime);
          /* eslint-enable no-console*/
        }

        /* remove when full deployment is done */
        if (typeof notificationMessage === 'number') {
          return setNotification({
            notificationType: notificationMessage,
            correlationId,
            receivedDateTime,
            conditionCategory: null,
          });
        }
        setNotification({
          notificationType: notificationMessage?.notificationType,
          correlationId,
          receivedDateTime,
          conditionCategory: notificationMessage?.conditionCategory,
        });

        if (
          notificationMessage.notificationType ===
          SIGNAL_R_STATUS.RULE_EVALUATION_COMPLETED
        ) {
          queryClient.invalidateQueries({ queryKey: loanSummaryKey });
        }
      },
    );

    connectionRef.current = connection;

    // start connection
    /* c8 ignore next */
    connection.onclose(() => setIsConnected(false));
    connection.onreconnecting(() => setIsConnected(false));
    connection.onreconnected(() => setIsConnected(true));

    connection
      .start()
      .then(function () {
        setIsConnected(true);
        // eslint-disable-next-line no-console
        console.log('Connected to Web Notification Service (SignalR hub).');
      })
      .catch(function (err) {
        // eslint-disable-next-line no-console
        console.log(
          'Error connecting to Web Notification Service (SignalR hub): ',
          err,
        );
        globalThis.dtrum?.reportError(err);
      });
  }, [
    isAuthorized,
    obfuscatedLoanIdentifier,
    user?.access_token,
    queryClient,
    setIsConnected,
  ]);

  useEffect(() => {
    startConnection();
    return () => {
      // Close connection when component unmounts
      connectionRef.current?.stop();
      connectionRef.current = null;
      setIsConnected(false);
    };
  }, [startConnection, setIsConnected]);

  // Chrome 149+: bfcache now closes active WebSockets on page entry (ws.onclose
  // fires). Reconnect and invalidate loan summary on restore — the notification
  // may have fired into an empty group during the disconnect window.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        connectionRef.current?.stop?.();
        connectionRef.current = null;
        startConnection();
        queryClient.invalidateQueries({ queryKey: loanSummaryKey });
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [startConnection, queryClient]);

  return notification;
};

export default useSignalR;
